import { useState, useMemo, useCallback } from 'react';

const compareValues = (a, b, dir) => {
  const aNull = a === null || a === undefined || (typeof a === 'number' && Number.isNaN(a));
  const bNull = b === null || b === undefined || (typeof b === 'number' && Number.isNaN(b));
  // Nulls always sort last regardless of direction.
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === 'string' && typeof b === 'string') {
    const cmp = a.localeCompare(b, undefined, { sensitivity: 'base' });
    return dir === 'asc' ? cmp : -cmp;
  }
  if (a < b) return dir === 'asc' ? -1 : 1;
  if (a > b) return dir === 'asc' ? 1 : -1;
  return 0;
};

export const useSortableTable = ({ rows, defaultKey = null, defaultDir = 'asc', accessors = {}, firstDir = {} } = {}) => {
  const [sort, setSort] = useState({ key: defaultKey, dir: defaultDir });

  const toggleSort = useCallback((key) => {
    setSort((prev) => {
      const initial = firstDir[key] || 'desc';
      const opposite = initial === 'desc' ? 'asc' : 'desc';
      if (prev.key !== key) return { key, dir: initial };
      if (prev.dir === initial) return { key, dir: opposite };
      return { key: defaultKey, dir: defaultDir };
    });
  }, [defaultKey, defaultDir, firstDir]);

  const sortedRows = useMemo(() => {
    if (!sort.key) return rows;
    const accessor = accessors[sort.key] || ((row) => row[sort.key]);
    const copy = [...rows];
    copy.sort((a, b) => compareValues(accessor(a), accessor(b), sort.dir));
    return copy;
  }, [rows, sort, accessors]);

  const setSortDirect = useCallback((key, dir) => {
    if (!key) { setSort({ key: defaultKey, dir: defaultDir }); return; }
    setSort({ key, dir });
  }, [defaultKey, defaultDir]);

  return { sortedRows, sortKey: sort.key, sortDir: sort.dir, toggleSort, setSort: setSortDirect };
};

export default useSortableTable;
